import { useEffect, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { PageContainer } from "@/ui/containers/PageContainer";

import { Button, HStack, Text } from "@chakra-ui/react";
import { Table, TableProps } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";

import { getStockStatus } from "@/utils/getStatusStock";
import { useGetErpProducts } from "../useCases/useGetErpProducts";
import { Pagination } from "@/core/common/types";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { useAuth } from "@/shared/session";
import { useToggleEnabledErpProduct } from "../useCases/useToggleEnabledErpProduct";
import { LuRefreshCw } from "react-icons/lu";
import ConfirmDialogSyncStock from "../components/ConfirmDialogSyncStock";
import ConfirmDialogSyncAllStock from "../components/ConfirmDialogSyncAllStock";
import ErpProductAdjustmentsModal from "../components/ErpProductAdjustmentsModal";
import { ErpProduct } from "@/shared/core-products";
import { FilterChips } from "@/ui/tables/FilterChips";
import { STOCK_FILTERS, type StockFilter } from "@/utils/stockFilters";
import { TableFilters } from "@/ui/filters/TableFilters";
import {
  countActiveFilters,
  getOption,
  type FilterValues,
} from "@/ui/filters/filterValues";
import { useStorageFilterField } from "@/core/storages/useCases/useStorageFilterField";

const getColumns = ({
  handleToggleEnabled,
  handleOnClickAdjustments,
}: {
  handleToggleEnabled: (erpProduct: ErpProduct) => void;
  handleOnClickAdjustments: (erpProduct: ErpProduct) => void;
}): TableProps<ErpProduct>["headers"] => [
  {
    label: "Código",
    itemLabel: (item) => <Text fontWeight="semibold">{item.erpCode}</Text>,
    sortable: true,
    sortKey: "erpCode",
  },
  {
    label: "Descripción",
    itemLabel: (item) => item.product.description,
    sortable: true,
    sortKey: "description",
  },
  {
    label: "Ubicación",
    itemLabel: (item) => item.product.location,
  },
  {
    label: "Unidad",
    itemLabel: (item) => item.product.unit,
  },
  {
    label: "Stock",
    sortable: true,
    sortKey: "stock",
    itemLabel: (item) =>
      getStockStatus(item.product?.stock, item.product?.minimumStock),
  },
  {
    label: "Stock ERP",
    sortable: true,
    sortKey: "erpStock",
    itemLabel: (item) => item.erpStock,
  },
  {
    label: "Conversor",
    itemLabel: (item) => item.product.converter,
  },
  {
    label: "Tipo de conversión",
    itemLabel: (item) => item.product?.converterType?.name ?? "",
  },
  {
    label: "Stock mínimo",
    itemLabel: (item) => item.product.minimumStock,
  },
  {
    label: "Bodega",
    itemLabel: (item) => item.product.storage.name ?? "",
  },
  {
    label: "Reajustes",
    itemLabel: (item: ErpProduct) => (
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleOnClickAdjustments(item)}
      >
        Ver reajustes
      </Button>
    ),
  },
  {
    label: "Estado",
    itemLabel: (item: ErpProduct) => (
      <Button
        variant="ghost"
        size="sm"
        colorPalette={item.enabled ? "gray" : "green"}
        color={item.enabled ? "fg.muted" : undefined}
        onClick={() => handleToggleEnabled(item)}
      >
        {item.enabled ? "Deshabilitar" : "Habilitar"}
      </Button>
    ),
  },
];

export const ErpProductPage = () => {
  const { session } = useAuth();
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const [selectedErpProduct, setSelectedErpProduct] =
    useState<ErpProduct | null>(null);
  const [isOpenDialogSync, setIsOpenDialogSync] = useState(false);
  const [isOpenDialogSyncAll, setIsOpenDialogSyncAll] = useState(false);
  const [isOpenAdjustmentsModal, setIsOpenAdjustmentsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [showDisabled, setShowDisabled] = useState<boolean>(false);
  const [stockFilter, setStockFilter] = useState<StockFilter>("all");
  const storageField = useStorageFilterField();
  const [extraFilters, setExtraFilters] = useState<FilterValues>({});
  const storageId = getOption(extraFilters, "storage")?.value;

  const { mutate: toggleEnabledErpProduct } = useToggleEnabledErpProduct();
  const {
    data: erpProducts = [],
    isPending,
    pagination: metaErpProducts,
  } = useGetErpProducts({
    page: pagination.page,
    pageSize: pagination.pageSize,
    searchTerm: debouncedSearchTerm,
    ...order,
    zoneId: session?.zone ?? undefined,
    showDisabled,
    storageId,
    stockStatus: stockFilter === "all" ? undefined : stockFilter,
  });

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = async (erpProduct: ErpProduct) => {
    const ok = await confirmToggle(
      erpProduct.enabled,
      `el producto ${erpProduct.erpCode ?? erpProduct.description}`,
    );
    if (!ok) return;
    toggleEnabledErpProduct(
      {
        id: erpProduct.id,
        enabled: !erpProduct.enabled,
      },
      {
        onSuccess: () =>
          notify.success(
            erpProduct.enabled
              ? "Producto deshabilitado"
              : "Producto habilitado",
          ),
        onError: (error) =>
          notify.error("No se pudo actualizar el producto", error),
      },
    );
  };

  const handleOnClickAdjustments = (erpProduct: ErpProduct) => {
    setSelectedErpProduct(erpProduct);
    setIsOpenAdjustmentsModal(true);
  };

  const handleOnSortChange = (sort: string, order: string) => {
    setOrder({ sort, order });
  };
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, stockFilter, storageId, showDisabled]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [session?.zone]);
  return (
    <PageContainer
      display={"flex"}
      flexDir={"column"}
      gap={4}
      title="Productos ERP"
      description="Visualiza los productos ERP (Totvs) y su stock en las bodegas."
      actions={
        <Button
          colorPalette="brand"
          onClick={() => setIsOpenDialogSyncAll(true)}
        >
          <LuRefreshCw />
          Sincronizar stock con Totvs
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por código ERP o descripción…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        page={pagination.page}
        hasActiveFilters={
          stockFilter !== "all" || countActiveFilters(extraFilters) > 0
        }
        onClearFilters={() => {
          setStockFilter("all");
          setExtraFilters({});
        }}
        filters={
          <HStack gap={1.5} flexWrap="wrap">
            <FilterChips
              options={STOCK_FILTERS}
              value={stockFilter}
              onChange={setStockFilter}
              counts={metaErpProducts?.countByStockStatus}
            />
            <TableFilters
              fields={[storageField]}
              value={extraFilters}
              onChange={setExtraFilters}
            />
          </HStack>
        }
        emptyDescription="Los productos ERP se cargan desde la sincronización con el ERP."
        data={erpProducts}
        pages={metaErpProducts?.totalPages}
        totalItems={metaErpProducts?.count}
        itemsLabel="productos"
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        onShowDisabledChange={(value) => setShowDisabled(value)}
        showDisabledCheck
        onSearch={(value) => setSearchTerm(value)}
        onSortChange={handleOnSortChange}
        showDefaultActions={false}
        headers={getColumns({
          handleToggleEnabled,
          handleOnClickAdjustments,
        })}
        extractItemKey={(item) => item.id}
        hiddenColumnsKey="erpProducts"
        actions={[
          {
            label: "Sincronizar stock",
            onClick: (item) => {
              setSelectedErpProduct(item as ErpProduct);
              setIsOpenDialogSync(true);
            },
            icon: <LuRefreshCw size={18} />,
          },
        ]}
      />
      {selectedErpProduct && (
        <ConfirmDialogSyncStock
          isOpen={isOpenDialogSync}
          onClose={() => setIsOpenDialogSync(false)}
          oldStock={selectedErpProduct.product.stock}
          newStock={selectedErpProduct.erpStock}
          erpProductId={selectedErpProduct.id}
        />
      )}
      <ConfirmDialogSyncAllStock
        isOpen={isOpenDialogSyncAll}
        onClose={() => setIsOpenDialogSyncAll(false)}
      />
      {selectedErpProduct && (
        <ErpProductAdjustmentsModal
          isOpen={isOpenAdjustmentsModal}
          onClose={() => setIsOpenAdjustmentsModal(false)}
          erpProduct={selectedErpProduct}
        />
      )}
    </PageContainer>
  );
};
